import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Direction, ValidationSeverity, type MessageAnalysisResult, type MessageEvent } from '@k-ucap/shared';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { createMessageAnalysisService } from '@/domain/message/MessageAnalysisService.js';

export interface AnalyzerFilter {
  search: string;
  messageType: string;
  direction: string;
  validationStatus: string;
  sourceRole: string;
  targetRole: string;
  runId: string;
}

const initialFilter: AnalyzerFilter = {
  search: '',
  messageType: '',
  direction: '',
  validationStatus: '',
  sourceRole: '',
  targetRole: '',
  runId: '',
};

export const useAnalyzer = () => {
  const { messageEventRepository } = useRepositories();
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<MessageEvent[]>([]);
  const [filter, setFilter] = useState<AnalyzerFilter>(() => ({
    ...initialFilter,
    runId: searchParams.get('runId') ?? '',
  }));
  const [selectedEvent, setSelectedEvent] = useState<MessageEvent | null>(null);
  const [analysisResult, setAnalysisResult] = useState<MessageAnalysisResult | null>(null);
  const [autoAnalyze, setAutoAnalyze] = useState(true);
  const [autoFollow, setAutoFollow] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const analysisService = useMemo(() => createMessageAnalysisService(), []);
  const analysisCache = useRef<Map<string, MessageAnalysisResult>>(new Map());
  const userSelectedRef = useRef(false);

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      const searchOk = filter.search
        ? [String(e.messageType), e.name, e.sourceRole, e.targetRole].some((v) =>
            v?.toLowerCase().includes(filter.search.toLowerCase())
          )
        : true;
      const typeOk = filter.messageType ? String(e.messageType) === filter.messageType : true;
      const dirOk = filter.direction ? e.direction === filter.direction : true;
      const statusOk = filter.validationStatus ? e.validation === filter.validationStatus : true;
      const sourceOk = filter.sourceRole ? e.sourceRole === filter.sourceRole : true;
      const targetOk = filter.targetRole ? e.targetRole === filter.targetRole : true;
      const runOk = filter.runId ? e.runId === filter.runId : true;
      return searchOk && typeOk && dirOk && statusOk && sourceOk && targetOk && runOk;
    });
  }, [events, filter]);

  const runAnalysis = useCallback(
    (event: MessageEvent, allEvents: MessageEvent[]) => {
      const cached = analysisCache.current.get(event.id);
      if (cached) {
        setAnalysisResult(cached);
        return cached;
      }
      const previousEvents = allEvents
        .filter((e) => e.id !== event.id && new Date(e.timestamp).getTime() <= new Date(event.timestamp).getTime())
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 100);
      const result = analysisService.analyze(event, { previousEvents });
      analysisCache.current.set(event.id, result);
      setAnalysisResult(result);
      return result;
    },
    [analysisService]
  );

  const selectEvent = useCallback(
    (event: MessageEvent) => {
      userSelectedRef.current = true;
      setSelectedEvent(event);
      setError(null);
      runAnalysis(event, events);
      setSearchParams({ messageEventId: event.id }, { replace: true });
    },
    [events, runAnalysis, setSearchParams]
  );

  const clearSelection = useCallback(() => {
    userSelectedRef.current = false;
    setSelectedEvent(null);
    setAnalysisResult(null);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (!selectedProject) {
      setEvents([]);
      return;
    }
    const unsubscribe = messageEventRepository.subscribeToProject?.(selectedProject.id, (loaded) => {
      setEvents(loaded);
    });
    if (!unsubscribe) {
      messageEventRepository
        .findByProjectId(selectedProject.id)
        .then((loaded) => setEvents(loaded))
        .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    }
    return () => unsubscribe?.();
  }, [messageEventRepository, selectedProject]);

  useEffect(() => {
    if (events.length === 0) return;

    const urlEventId = searchParams.get('messageId') ?? searchParams.get('messageEventId');
    if (urlEventId && !selectedEvent) {
      const found = events.find((e) => e.id === urlEventId);
      if (found) {
        setSelectedEvent(found);
        runAnalysis(found, events);
        userSelectedRef.current = true;
        return;
      }
    }

    if (selectedEvent) {
      const stillExists = events.find((e) => e.id === selectedEvent.id);
      if (stillExists) {
        if (autoFollow && events[0]?.id !== selectedEvent.id && !userSelectedRef.current) {
          const latest = events[0];
          setSelectedEvent(latest);
          runAnalysis(latest, events);
        }
        return;
      }
      if (autoFollow) {
        const latest = events[0];
        setSelectedEvent(latest);
        runAnalysis(latest, events);
        return;
      }
    }

    if (autoAnalyze && !userSelectedRef.current) {
      const latest = events[0];
      setSelectedEvent(latest);
      runAnalysis(latest, events);
    }
  }, [events, searchParams, selectedEvent, autoAnalyze, autoFollow, runAnalysis]);

  const messageTypes = useMemo(() => Array.from(new Set(events.map((e) => String(e.messageType)))).sort(), [events]);
  const sourceRoles = useMemo(
    () => Array.from(new Set(events.map((e) => e.sourceRole).filter(Boolean))).sort(),
    [events]
  );
  const targetRoles = useMemo(
    () => Array.from(new Set(events.map((e) => e.targetRole).filter(Boolean))).sort(),
    [events]
  );

  const updateFilter = useCallback((patch: Partial<AnalyzerFilter>) => {
    setFilter((prev) => ({ ...prev, ...patch }));
  }, []);

  const statusCounts = useMemo(() => {
    return {
      pass: events.filter((e) => e.validation === ValidationSeverity.PASS).length,
      warn: events.filter((e) => e.validation === ValidationSeverity.WARN).length,
      fail: events.filter((e) => e.validation === ValidationSeverity.FAIL).length,
      unknown: events.filter((e) => e.validation === ValidationSeverity.UNKNOWN).length,
      unsupported: events.filter((e) => e.validation === ValidationSeverity.UNSUPPORTED).length,
    };
  }, [events]);

  return {
    events,
    filteredEvents,
    selectedEvent,
    analysisResult,
    filter,
    updateFilter,
    autoAnalyze,
    setAutoAnalyze,
    autoFollow,
    setAutoFollow,
    selectEvent,
    clearSelection,
    runAnalysis: () => selectedEvent && runAnalysis(selectedEvent, events),
    messageTypes,
    sourceRoles,
    targetRoles,
    statusCounts,
    error,
    directions: Object.values(Direction),
  };
};
